import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X } from 'lucide-react';
import wizardImage from '@/assets/wizard-white-bg.png';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
interface FloatingTailorProps {
  onClose: () => void;
  onAIEdit?: (request: string) => void;
}
export const FloatingTailor: React.FC<FloatingTailorProps> = ({
  onClose,
  onAIEdit
}) => {
  const [position, setPosition] = useState({
    x: 50,
    y: 50
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({
    x: 0,
    y: 0
  });
  const [aiRequest, setAiRequest] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const tailorRef = useRef<HTMLDivElement>(null);
  
  const handleMouseDown = (e: React.MouseEvent) => {
    if (tailorRef.current) {
      const rect = tailorRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleAIRequest = async () => {
    if (!aiRequest.trim()) {
      toast.error('Please enter a request');
      return;
    }

    if (onAIEdit) {
      setIsProcessing(true);
      try {
        await onAIEdit(aiRequest);
        setAiRequest('');
        toast.success('Request sent!');
      } catch (error) {
        toast.error('Failed to process request');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAIRequest();
    }
  };
  return <div ref={tailorRef} className="fixed z-50 animate-fade-in" style={{
    left: `${position.x}px`,
    top: `${position.y}px`,
    transform: isDragging ? 'scale(1.05)' : 'scale(1)',
    transition: isDragging ? 'none' : 'transform 0.2s ease-out'
  }}>
      <Card className="w-56 shadow-xl border bg-white hover:shadow-2xl transition-all duration-300">
        <CardContent className="p-0">
          {/* Header with close button */}
          <div className="flex justify-between items-center p-2 bg-gradient-hero rounded-t-lg py-0">
            <div className="flex-1 cursor-move" onMouseDown={handleMouseDown}>
              {/* Drag indicator */}
              <div className="flex justify-center">
                <div className="w-8 h-1 bg-white/30 rounded-full"></div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0 text-white hover:bg-white/20 rounded-full">
              <X className="w-3 h-3" />
            </Button>
          </div>

          {/* Wizard Character */}
          <div className="p-4 text-center bg-white relative">
            <div className="relative inline-block">
              {/* Compact Wizard */}
              <div className="w-32 h-40 mx-auto mb-2 flex items-center justify-center relative">
                <img 
                  src={wizardImage} 
                  alt="Tailor the Wizard" 
                  className="w-full h-full object-contain" 
                />
              </div>
              
              {/* Magic sparkles */}
              <div className="absolute -top-4 -right-4 text-yellow-400 animate-bounce text-2xl">✨</div>
              <div className="absolute top-8 -left-6 text-blue-400 animate-pulse text-2xl">⭐</div>
              <div className="absolute -bottom-4 left-8 text-purple-400 animate-bounce text-2xl" style={{
              animationDelay: '0.5s'
            }}>💫</div>
              <div className="absolute top-16 right-2 text-green-400 animate-pulse text-lg" style={{
              animationDelay: '0.8s'
            }}>🌟</div>
              <div className="absolute bottom-12 -right-4 text-pink-400 animate-bounce text-lg" style={{
              animationDelay: '1s'
            }}>⚡</div>
            </div>

            {/* Name */}
            <h3 className="font-bold text-lg text-foreground mb-1">Tailor the Resume Wizard</h3>
            <p className="text-xs text-muted-foreground mb-3 px-0 py-0">Your AI Resume Assistant</p>
          </div>

          {/* AI Input Box */}
          <div className="p-2 bg-white rounded-b-lg">
            <div className="bg-primary-light rounded-xl p-3 border border-primary/20 relative shadow-lg">
              {/* Speech bubble arrow */}
              <div className="absolute -top-2 left-6 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-primary-light drop-shadow-sm"></div>
              
              <p className="text-sm font-semibold text-primary-dark mb-2">
                What edits can I make?
              </p>
              
              <div className="space-y-2">
                <Textarea
                  value={aiRequest}
                  onChange={(e) => setAiRequest(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your editing request..."
                  className="min-h-[60px] text-sm resize-none border-primary/30 focus:border-primary bg-white"
                  disabled={isProcessing}
                />
                <Button 
                  onClick={handleAIRequest}
                  disabled={!aiRequest.trim() || isProcessing}
                  className="w-full h-8 text-xs bg-primary hover:bg-primary-hover"
                  size="sm"
                >
                  {isProcessing ? 'Processing...' : 'Make Edits'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
};